// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/StatusOs.tsx
//  Cadastro de Status da Ordem de Serviço (espelho do FrmCadStatusOS desktop).
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useStatusOs } from '@/features/operacional/hooks/useStatusOs';
import type { StatusOrdemServico, SalvarStatusOrdemServicoRequest } from '@/features/operacional/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2 } from 'lucide-react';

const EMPTY: SalvarStatusOrdemServicoRequest = {
  descricao: '', cor: '#2563EB', contrato: '',
  isAberta: false, isAndamento: false, isAgendar: false,
  isFaturar: false, isFechada: false, isPermissao: false,
  isEmailAutomatico: false, emailAssunto: '', emailCorpo: '', isInativo: false,
};

const FLAGS: { key: keyof SalvarStatusOrdemServicoRequest; label: string }[] = [
  { key: 'isAberta', label: 'Abertura' },
  { key: 'isAndamento', label: 'Em andamento' },
  { key: 'isAgendar', label: 'Agendamento' },
  { key: 'isFaturar', label: 'Faturamento' },
  { key: 'isFechada', label: 'Fechamento' },
  { key: 'isPermissao', label: 'Exige permissão' },
];

export default function StatusOs() {
  const { statusList, isLoading, criarStatus, atualizarStatus, removerStatus } = useStatusOs();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<StatusOrdemServico | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [form, setForm] = useState<SalvarStatusOrdemServicoRequest>(EMPTY);
  const [busca, setBusca] = useState('');

  const filtrados = busca
    ? statusList.filter((s) => s.descricao?.toLowerCase().includes(busca.toLowerCase()))
    : statusList;

  const openDialog = (s?: StatusOrdemServico) => {
    if (s) {
      setEditing(s);
      setForm({
        descricao: s.descricao, cor: s.cor ?? '#2563EB', contrato: s.contrato ?? '',
        isAberta: s.isAberta, isAndamento: s.isAndamento, isAgendar: s.isAgendar,
        isFaturar: s.isFaturar, isFechada: s.isFechada, isPermissao: s.isPermissao,
        isEmailAutomatico: s.isEmailAutomatico, emailAssunto: s.emailAssunto ?? '',
        emailCorpo: s.emailCorpo ?? '', isInativo: s.isInativo,
      });
    } else {
      setEditing(null);
      setForm(EMPTY);
    }
    setDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.descricao.trim()) return;
    if (editing) atualizarStatus({ id: editing.id, req: form });
    else criarStatus(form);
    setDialogOpen(false);
  };

  const set = <K extends keyof SalvarStatusOrdemServicoRequest>(k: K, v: SalvarStatusOrdemServicoRequest[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  if (isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Status de Ordem de Serviço</h1>
          <p className="text-muted-foreground">Cadastro dos status (etapas) das OS, com cor e comportamento.</p>
        </div>
        <Button onClick={() => openDialog()}>
          <Plus className="h-4 w-4 mr-2" />Novo Status
        </Button>
      </div>

      <div className="max-w-sm">
        <Input placeholder="Buscar por descrição..." value={busca} onChange={(e) => setBusca(e.target.value)} />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[60px]">Cor</TableHead>
              <TableHead>Descrição</TableHead>
              <TableHead>Comportamento</TableHead>
              <TableHead className="w-[90px]">Situação</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtrados.map((s) => (
              <TableRow key={s.id}>
                <TableCell>
                  <span className="inline-block h-5 w-5 rounded-full border"
                    style={{ backgroundColor: s.cor ?? 'transparent' }} />
                </TableCell>
                <TableCell className="font-medium">{s.descricao}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {FLAGS.filter((f) => (s as unknown as Record<string, boolean>)[f.key]).map((f) => (
                      <Badge key={f.key} variant="secondary" className="text-xs">{f.label}</Badge>
                    ))}
                    {s.isEmailAutomatico && <Badge variant="outline" className="text-xs">E-mail</Badge>}
                  </div>
                </TableCell>
                <TableCell>{s.isInativo ? <span className="text-muted-foreground">Inativo</span> : 'Ativo'}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openDialog(s)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleteId(s.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtrados.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  {busca ? 'Nenhum status encontrado.' : 'Nenhum status cadastrado.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Status' : 'Novo Status'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-[1fr_auto] gap-3">
              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição *</Label>
                <Input id="descricao" value={form.descricao}
                  onChange={(e) => set('descricao', e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cor">Cor</Label>
                <Input id="cor" type="color" className="h-10 w-16 p-1"
                  value={form.cor ?? '#2563EB'} onChange={(e) => set('cor', e.target.value)} />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="contrato">Contrato / referência</Label>
              <Input id="contrato" value={form.contrato ?? ''} onChange={(e) => set('contrato', e.target.value)} />
            </div>

            <div className="grid grid-cols-2 gap-2">
              {FLAGS.map((f) => (
                <label key={f.key} className="flex items-center gap-2 text-sm">
                  <Switch checked={!!form[f.key]} onCheckedChange={(v) => set(f.key, v as never)} />
                  {f.label}
                </label>
              ))}
            </div>

            <div className="space-y-3 rounded-md border p-3">
              <label className="flex items-center gap-2 text-sm font-medium">
                <Switch checked={!!form.isEmailAutomatico} onCheckedChange={(v) => set('isEmailAutomatico', v)} />
                Enviar e-mail automático ao entrar neste status
              </label>
              {form.isEmailAutomatico && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="assunto">Assunto do e-mail</Label>
                    <Input id="assunto" value={form.emailAssunto ?? ''}
                      onChange={(e) => set('emailAssunto', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="corpo">Corpo do e-mail</Label>
                    <Textarea id="corpo" rows={3} value={form.emailCorpo ?? ''}
                      onChange={(e) => set('emailCorpo', e.target.value)} />
                  </div>
                </>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <Switch checked={!!form.isInativo} onCheckedChange={(v) => set('isInativo', v)} />
              Inativo
            </label>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir status</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este status? A operação não poderá ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (deleteId) { removerStatus(deleteId); setDeleteId(null); } }}>
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
